import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, ShieldCheck, Bell, Clock, CheckCircle2, ArrowRight,
  ArrowUpRight, Menu, X, Check, Zap, Phone, Repeat,
  MessageCircle, QrCode, CircleUserRound, Mail,
  BarChart3, Smartphone, DoorOpen, UserCog, FileSpreadsheet, MonitorSmartphone,
  Ban, Truck, Vote, Car, Wrench, Trees, Siren, ScrollText, Search, Home,
} from 'lucide-react';
import onboardingService from '../api/onboarding.service';
import './Landing.css';
import './ajustes-portadas.css';

/**
 * Portada comercial del sistema de Control Residencial.
 *
 * Misma estructura y hoja de estilos que la de MediClinic Pro —son productos
 * hermanos y deben verse de la misma casa—, con el contenido de este sistema.
 *
 * Los precios NO están escritos aquí: se leen de `GET /onboarding/plans`, que
 * es el mismo sitio de donde los toma el cobro. Así la página no puede
 * contradecir lo que realmente se cobra, que es la forma más rápida de perder
 * la confianza de alguien que ya estaba decidido.
 *
 * Quien prefiera preguntar antes de pagar tiene `/solicitar`, enlazado abajo.
 */

/* ────────────────────────────────────────────────────────────────────────
 * EL TEXTO DE LA PORTADA
 *
 * Quien lee esto es la junta directiva de una ADESCO o quien administra el
 * condominio. No es programador y no está comprando un programa: está
 * tratando de resolver un problema de su comunidad, casi siempre sin cobrar
 * por hacerlo. Así que cada línea empieza por lo que GANA la residencial, y
 * sólo después explica cómo.
 *
 * LA PRUEBA QUE TIENE QUE PASAR CADA FRASE: si un miembro de la directiva la
 * lee en voz alta en una asamblea, ¿los vecinos entienden para qué sirve?
 * «Queda constancia de a qué hora fue cada cosa» no la pasa. «Cuando alguien
 * pregunte quién entró el sábado, la respuesta sale en segundos» sí.
 *
 * TRES COSAS QUE LAS VERSIONES ANTERIORES CONTABAN MAL:
 *
 * 1. Describían mecánica —quién toca qué botón— en vez de beneficio.
 * 2. Daban por hecho que todo gira alrededor del código QR. No es así: el
 *    guardia puede anotar a quien llegue sin avisar y marcar la entrada y la
 *    salida desde su lista, sin escanear nada. El QR es un atajo opcional.
 * 3. No mencionaban el pase que el residente le manda al visitante por
 *    WhatsApp, que es lo que más rápido se entiende al explicarlo.
 *
 * ── LO QUE CAMBIÓ EL 21 DE SEPTIEMBRE DE 2026 ───────────────────────────
 *
 * El sistema dejó de ser sólo control de acceso. A las visitas se les sumaron
 * las restricciones, los incidentes, los vehículos, las amenidades, el
 * mantenimiento, la paquetería, las entregas, los comunicados, las encuestas,
 * los documentos, la junta directiva y los proveedores.
 *
 * Eso obligó a cambiar la promesa de la portada. «Control de acceso» se queda
 * corto, y al mismo tiempo **no se puede convertir esto en una lista de
 * veintitantas funciones**: una directiva no compra una lista, compra la
 * sensación de que va a dejar de perseguir cosas por WhatsApp.
 *
 * De ahí la estructura nueva:
 *
 *  · El encabezado promete lo grande: dejar de administrar por WhatsApp.
 *  · «Tres personas» organiza TODO el sistema en tres columnas —el residente,
 *    la caseta, la directiva—, que es como la gente lo vive. Sin esa sección,
 *    los módulos nuevos serían un muro de tarjetas que nadie termina de leer.
 *  · «Funciones» ya no intenta listarlo todo: enseña doce, las que más
 *    rápido se entienden.
 *  · Entregas y «Cuando algo pasa» tienen sección propia porque son las dos
 *    cosas nuevas que cualquiera reconoce al instante: el repartidor parado
 *    en la caseta, y el día que hay que averiguar qué ocurrió.
 *
 * DE DÓNDE SALE ESTE CONTENIDO. De `claude.md` (la documentación del
 * sistema), del roadmap de producto y de `VIDEO_DEMO_GUION_ADESCO.md`, que es
 * el guion que el dueño ya usaba con esta misma audiencia y donde están, con
 * sus palabras, el problema («es difícil verificar si un visitante está
 * realmente autorizado») y la objeción de presupuesto («sin comprar equipos
 * caros»).
 *
 * ⚠️ LO QUE NO SE PUEDE PROMETER AQUÍ.
 *
 * Nada que el sistema no haga hoy. La regla ya se rompió una vez —se
 * anunciaban avisos de visitante frecuente que ningún controlador emitía— y
 * por eso conviene decir qué SÍ se comprobó antes de escribir esta versión:
 *
 *  · Los avisos de visitante frecuente y de pases acumulados **ya se emiten**
 *    (`services/tareas.service.js`). Por eso ahora sí aparecen.
 *  · La exportación a Excel y PDF existe, pero **sólo en el resumen de
 *    visitas** (`VisitSummary.jsx`). El texto dice «el movimiento de
 *    visitas», no «los reportes», que sería más de lo que hay.
 *  · Las fotografías vienen APAGADAS de fábrica. Se anuncian como una
 *    decisión de la comunidad, nunca como algo que el sistema hace solo.
 *  · El guardia **no** tiene operación sin conexión. No se menciona.
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * TRES PERSONAS, UN SISTEMA.
 *
 * La sección que sostiene la portada nueva. Un condominio no compra
 * «módulos»: compra que su gente deje de perseguir cosas. Y la gente son
 * tres: quien vive ahí, quien está en la caseta y quien tiene que responder
 * en la asamblea.
 *
 * Cada lista está ordenada por lo que más se usa, no por lo que más impresiona.
 */
const PERSONAS = [
  {
    icon: Home,
    rol: 'El residente',
    lema: 'Desde su teléfono, esté donde esté',
    puntos: [
      'Autoriza a quien espera sin tener que estar en la casa',
      'Le manda el pase por WhatsApp y su visita no hace fila',
      'Se entera en cuanto llega, y otra vez cuando se va',
      'Deja dicho de una vez qué hacer con sus entregas',
      'Reserva la casa club, la cancha o el salón',
      'Reporta la lámpara fundida de su calle, con foto',
      'Vota las decisiones de la comunidad desde el sofá',
      'Tiene el reglamento y las actas siempre a mano',
    ],
  },
  {
    icon: ShieldCheck,
    rol: 'La caseta',
    lema: 'Sin cuaderno y sin llamar a las casas',
    puntos: [
      'Ve la lista del día y marca entrada y salida',
      'Registra a quien llega sin avisar, y el residente autoriza al momento',
      'Le avisa si esa persona o esa placa están reportadas',
      'Busca por nombre, casa, placa o teléfono en un solo campo',
      'Consulta qué hacer con una entrega sin levantar el teléfono',
      'Reporta incidentes y deja constancia con fotografía',
      'Recibe paquetería y anota quién la retiró',
      'Marca su turno y sabe qué punto le toca',
    ],
  },
  {
    icon: BarChart3,
    rol: 'La directiva',
    lema: 'Para gobernar con datos y no con versiones',
    puntos: [
      'Ve cuánta gente entra, a qué horas y por qué sector',
      'Una bitácora que nadie puede editar después',
      'Publica comunicados y alertas, a todos o a un polígono',
      'Abre encuestas y decide sin esperar a la próxima asamblea',
      'Convoca, levanta acta y deja acuerdos con responsable y fecha',
      'Publica el reglamento y los estados de cuenta',
      'Registra proveedores con su personal autorizado',
      'Administra guardias, turnos y puntos de acceso',
    ],
  },
];

/**
 * Las doce funciones que se enseñan.
 *
 * Son doce y no veintiséis a propósito. Las escogidas son las que se
 * entienden sin explicación y las que resuelven una molestia que la directiva
 * ya tiene: la fila en la entrada, la llamada a la casa, el vecino que
 * reclama, la asamblea a la que no va nadie.
 */
const FUNCIONES = [
  {
    icon: MessageCircle,
    title: 'Su visita no hace fila en la entrada',
    description:
      'El residente le manda por WhatsApp un pase con la fecha y la casa. El visitante llega, lo muestra y pasa. Sin discutir en la caseta, sin esperar a que alguien conteste el teléfono y sin que el de atrás toque la bocina.',
    highlighted: true,
  },
  {
    icon: Smartphone,
    title: 'Llegó alguien sin avisar, y usted decide desde el teléfono',
    description:
      'El guardia lo anota en la caseta y le llega la pregunta a su celular: autoriza o no autoriza. El guardia ve la respuesta al instante. Esa es la llamada que hoy nadie contesta y que deja a la gente esperando en el portón.',
    highlighted: true,
  },
  {
    icon: Ban,
    title: 'Si esa persona no debe entrar, la caseta lo sabe',
    description:
      'La comunidad puede reportar a una persona o a una placa. Cuando alguien intenta entrar con ese nombre o ese vehículo, al guardia le salta el aviso antes de abrir. El guardia reporta; la directiva confirma. Nadie queda vetado por decisión de un turno de noche.',
  },
  {
    icon: Truck,
    title: 'El repartidor ya no obliga a llamar a la casa',
    description:
      'Usted deja dicho una sola vez qué hacer con sus entregas: dejarlas en recepción, subirlas a la puerta o no dejar pasar a nadie. El guardia escribe el número de casa y lee la respuesta. Se acabó la comida que se enfría mientras suena un teléfono que nadie contesta.',
    highlighted: true,
  },
  {
    icon: Clock,
    title: 'Un pase que dura lo que usted diga',
    description:
      'Una sola entrada, sólo hoy, tres días, una semana, hasta una fecha o permanente para la empleada y el jardinero. Y si cambió de opinión, lo revoca: el código deja de servir en ese mismo instante, con su motivo anotado.',
  },
  {
    icon: Car,
    title: 'Quién entró con esa placa, y en qué venía',
    description:
      'Cada visita queda con su forma de llegada —a pie, en carro o en moto— y con la placa. Los vehículos de los residentes tienen su propio registro. Cuando haya que averiguar quién entró con la P123-456, se escribe la placa y sale.',
  },
  {
    icon: Bell,
    title: 'El sistema le avisa lo que usted no alcanza a ver',
    description:
      'Si la misma persona lleva visitando su casa muchas veces al mes, le sugiere dejarla como visitante frecuente. Si se le acumularon pases sin usar, se lo dice. Y los pases que vencieron sin que nadie llegara se cierran solos.',
  },
  {
    icon: Search,
    title: 'Un solo campo para buscar cualquier cosa',
    description:
      'Nombre a medias, número de casa, tres dígitos de una placa, el teléfono o el código del pase. El guardia escribe lo que alcanzó a leer y el sistema busca en todo a la vez. Y si esa placa está reportada, sale de primero y en rojo.',
  },
  {
    icon: Trees,
    title: 'La casa club se reserva sin pelear en el grupo',
    description:
      'El residente ve qué días están libres y aparta el suyo; la administración aprueba. Se acabó el mensaje de las once de la noche reclamando que alguien se le adelantó, y el cumpleaños con dos familias en el mismo salón.',
  },
  {
    icon: Wrench,
    title: 'La lámpara fundida deja de perderse en el grupo',
    description:
      'El vecino la reporta con una foto y el reporte queda con su estado: recibido, en proceso, resuelto. La directiva ve de un vistazo qué está pendiente, y el vecino ve que su reporte no se perdió entre doscientos mensajes.',
  },
  {
    icon: Vote,
    title: 'Se decide sin esperar a la asamblea',
    description:
      'La directiva abre la pregunta y cada casa vota desde el teléfono. Y nadie ve los resultados hasta que ya votó: ver que una opción va ganando arrastra el voto de la gente, y una encuesta que se arrastra sola no sirve para decidir nada.',
  },
  {
    icon: ScrollText,
    title: 'El reglamento y las actas, donde siempre se encuentran',
    description:
      'Documentos, actas y estados de cuenta en un solo lugar, no en un grupo de WhatsApp donde a las tres semanas ya nadie los halla. Y las reuniones quedan con su acta y sus acuerdos, cada uno con responsable y fecha.',
  },
];

const BENEFICIOS = [
  {
    icon: Zap,
    title: 'La caseta deja de ser el cuello de botella',
    description:
      'El guardia no escribe, no llama a la casa ni hace esperar al de atrás: ve la lista y marca.',
  },
  {
    icon: Smartphone,
    title: 'El residente no tiene que estar en casa',
    description:
      'Autoriza desde donde esté y se entera cuando su visita llega, aunque ande fuera del país.',
  },
  {
    icon: Repeat,
    title: 'Cuando pasa algo, hay con qué responder',
    description:
      'Quién entró ese día, a qué casa y a qué hora. Con datos, no con lo que recuerde cada quien.',
  },
  {
    icon: ShieldCheck,
    title: 'Sus datos no se mezclan',
    description:
      'Otra residencial que use el mismo sistema no ve ni una de sus visitas. Nunca.',
  },
  {
    icon: MonitorSmartphone,
    title: 'Sin invertir en equipo',
    description:
      'Basta el celular del guardia. Computadora, laptop, tableta o teléfono: funciona en todos.',
  },
];

const PASOS = [
  {
    title: 'Regístrese',
    description: 'Llene los datos de su comunidad y de quien va a administrarla. Toma dos minutos.',
  },
  {
    title: 'Active su residencial',
    description: 'Pague en línea y su sistema queda listo al momento, con su cuenta de administración creada.',
  },
  {
    title: 'Dé de alta a su gente',
    description: 'Agregue a sus guardias y a sus residentes, y esa misma tarde empiezan a registrar.',
  },
];

const NAV = [
  { id: 'inicio', label: 'Inicio' },
  { id: 'personas', label: 'Para quién' },
  { id: 'funciones', label: 'Funciones' },
  { id: 'respaldo', label: 'Respaldo' },
  { id: 'seguridad', label: 'Seguridad' },
  { id: 'precios', label: 'Precios' },
  { id: 'contacto', label: 'Contacto' },
];

const WHATSAPP = 'https://wa.me/50368960231';
const WHATSAPP_MENSAJE = `${WHATSAPP}?text=Hola%2C%20me%20interesa%20el%20sistema%20de%20control%20residencial`;

const unsplash = (id, params) => `https://images.unsplash.com/${id}?auto=format&q=80&${params}`;

/**
 * Pexels, para las dos fotos de personas.
 *
 * Su licencia permite el uso comercial sin atribución, igual que la de
 * Unsplash. Se usa aquí porque las dos escenas que hacían falta —un repartidor
 * entregando y alguien mirando su teléfono en casa— no aparecían en Unsplash
 * con la calidad necesaria.
 *
 * ⚠️ LAS FOTOS SE MIRAN ANTES DE PONERLAS. Las dos que había aquí estaban
 * mal y nadie lo había notado: la de entregas era un código QR de Alipay con
 * texto en chino, y la de avisos una mansión de Nueva Inglaterra con hojas de
 * otoño — que es justo el tipo de foto que el dueño ya había rechazado
 * («se muestra una piscina y una mansión jajaja»). Un identificador de banco
 * de imágenes no dice nada sobre lo que se ve: hay que abrirla.
 */
const pexels = (id, ancho, alto) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${ancho}&h=${alto}&fit=crop`;

/**
 * LA FOTO DE LA ENTRADA VIGILADA.
 *
 * Es de Unsplash, cuya licencia permite el uso comercial sin atribucion. Lo
 * que se ve —caseta, pluma y porton— es literalmente lo que hace el sistema,
 * y por eso aparece en la portada, en el registro y en el acceso.
 *
 * Se usa con tres encuadres distintos porque cada hueco tiene otra forma: el
 * hero es una panoramica, la ficha de "quienes somos" pide la caseta de
 * cerca, y el cierre es un fondo detras de un velo oscuro. `fp-x`/`fp-y`
 * fijan que punto de la foto queda centrado y `fp-z` cuanto se acerca; los
 * recorta el CDN, no el navegador, asi que no se descarga de mas.
 */
const ENTRADA = 'photo-1775112077888-8fa36e9bbc51';
const caseta = (ancho, alto, z, x = 0.64, y = 0.58) =>
  unsplash(ENTRADA, `w=${ancho}&h=${alto}&fit=crop&crop=focalpoint&fp-x=${x}&fp-y=${y}&fp-z=${z}`);

const prefiereMenosMovimiento = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activo, setActivo] = useState('inicio');
  const [planes, setPlanes] = useState([]);
  const [anual, setAnual] = useState(false);
  const pageRef = useRef(null);
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);

  // Los precios se leen del servidor, nunca se escriben en la página.
  useEffect(() => {
    onboardingService
      .getPlans()
      .then((res) => setPlanes(res.data || []))
      .catch(() => setPlanes([]));
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Aparición de las secciones al llegar a ellas. Solo se oculta el contenido
  // cuando el observador existe: sin él (o sin JavaScript) todo queda visible.
  useEffect(() => {
    const root = pageRef.current;
    if (!root || !('IntersectionObserver' in window) || prefiereMenosMovimiento()) return undefined;
    root.dataset.revelar = 'true';
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible-landing');
          obs.unobserve(e.target);
        }
      }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    root.querySelectorAll('.revelar-landing').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Enlace activo del menú según la sección visible.
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActivo(e.target.id)),
      { rootMargin: '-45% 0px -50% 0px' },
    );
    NAV.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const cerrarMenu = useCallback(() => {
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  // Menú lateral: fondo congelado, Escape para cerrar y foco atrapado dentro.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const drawer = drawerRef.current;
    const enfocables = () => drawer?.querySelectorAll('a[href], button:not([disabled])') || [];
    enfocables()[0]?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrarMenu();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = enfocables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, cerrarMenu]);

  const irA = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({
      behavior: prefiereMenosMovimiento() ? 'auto' : 'smooth',
    });
    window.history.replaceState(null, '', id === 'inicio' ? window.location.pathname : `#${id}`);
  };

  // Cifras del hero: todas son hechos del producto, no cifras de marketing.
  /* Cifras del encabezado.
   *
   * Cuatro, y las cuatro comprobables. Una versión anterior anunciaba
   * «Excel y PDF» y «0 llamadas a la casa»: parecía que se vendían cursos de
   * ofimática. Lo que va aquí es lo que la residencial GANA, dicho en dos
   * palabras.
   *
   * · El panel del guardia lleva en pantalla quiénes entraron y no han salido.
   * · El aviso al residente sale en cuanto se marca la entrada.
   * · La bitácora no se puede editar: una corrección genera un registro nuevo.
   *   Ése es el argumento que la directiva necesita en una asamblea.
   * · No hace falta torniquete, lector ni cámara: basta la del teléfono. */
  const cifras = [
    { valor: 'En vivo', etiqueta: 'cuántos visitantes están dentro ahora' },
    { valor: 'Al instante', etiqueta: 'el residente sabe que llegó su visita' },
    { valor: 'Queda escrito', etiqueta: 'y después nadie lo puede borrar' },
    { valor: '$0', etiqueta: 'en equipos: basta el celular del guardia' },
  ];

  const intervalo = anual ? 'anual' : 'mensual';
  const planBase = planes.find((p) => p.tier === 'basic' && p.interval === intervalo);
  const planCompleto = planes.find((p) => p.tier === 'custom' && p.interval === intervalo);
  const periodo = anual ? 'año' : 'mes';

  return (
    <div className="page-landing" ref={pageRef}>
      {/* ==================== NAVEGACIÓN ==================== */}
      <header className={`navbar-landing${scrolled ? ' scrolled-landing' : ''}`}>
        <div className="container-landing navbar-inner-landing">
          <a href="#inicio" className="brand-landing" onClick={irA('inicio')}>
            <ShieldCheck className="brand-icon-landing" strokeWidth={2.25} aria-hidden="true" />
            <span>Control Residencial</span>
          </a>

          <nav className="nav-links-landing" aria-label="Secciones">
            {NAV.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={irA(id)}
                data-label={label}
                className={`nav-link-landing${activo === id ? ' nav-link-active-landing' : ''}`}
                aria-current={activo === id ? 'true' : undefined}
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="nav-actions-landing">
            <Link to="/login" className="nav-icon-landing" aria-label="Iniciar sesión" title="Iniciar sesión">
              <CircleUserRound strokeWidth={1.75} />
            </Link>
            <button
              ref={menuButtonRef}
              type="button"
              className="nav-icon-landing"
              onClick={() => setMenuOpen(true)}
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
              aria-controls="menu-landing"
            >
              <Menu strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Menú lateral */}
      <div
        className={`drawer-veil-landing${menuOpen ? ' open-landing' : ''}`}
        onClick={cerrarMenu}
        aria-hidden="true"
      />
      <aside
        id="menu-landing"
        ref={drawerRef}
        className={`drawer-landing${menuOpen ? ' open-landing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menú"
        inert={!menuOpen}
      >
        <div className="drawer-head-landing">
          <span className="brand-landing brand-dark-landing">
            <ShieldCheck className="brand-icon-landing" strokeWidth={2.25} aria-hidden="true" />
            <span>Control Residencial</span>
          </span>
          <button type="button" className="nav-icon-landing" onClick={cerrarMenu} aria-label="Cerrar menú">
            <X strokeWidth={1.75} />
          </button>
        </div>
        <nav className="drawer-nav-landing" aria-label="Menú principal">
          {NAV.map(({ id, label }, i) => (
            <a key={id} href={`#${id}`} onClick={irA(id)} className="drawer-link-landing">
              <span className="drawer-num-landing">{String(i + 1).padStart(2, '0')}</span>
              {label}
            </a>
          ))}
        </nav>
        <div className="drawer-foot-landing">
          <Link to="/adquirir" className="btn-landing btn-primary-landing btn-block-landing">
            Comience ahora <ArrowRight />
          </Link>
          <Link to="/login" className="btn-landing btn-outline-landing btn-block-landing">
            Iniciar sesión
          </Link>
          <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="drawer-contact-landing">
            <Phone /> +503 6896-0231
          </a>
        </div>
      </aside>

      <main>
        {/* ==================== HERO ==================== */}
        <section id="inicio" className="hero-landing" aria-labelledby="titulo-hero">
          <img
            className="hero-photo-landing"
            src={unsplash(ENTRADA, 'w=1920')}
            alt=""
            fetchPriority="high"
          />
          <div className="hero-shade-landing" aria-hidden="true" />

          <div className="container-landing hero-inner-landing">
            <div className="hero-copy-landing">
              {/* El titular.
                  Antes decía «el control de acceso... bajo control», y se
                  quedó corto: el sistema ya no es sólo la entrada. Esta
                  versión nombra el problema que una directiva reconoce sin
                  que nadie se lo explique — su comunidad se administra en un
                  grupo de WhatsApp donde todo se pierde a los tres días. */}
              <h1 id="titulo-hero" className="hero-title-landing">
                Deje de administrar su residencial por WhatsApp.
              </h1>

              <div className="hero-intro-landing">
                <span className="hero-label-landing">Control Residencial</span>
                <p>
                  Quién entra y quién lo autorizó. Las reservas de la casa club. La lámpara
                  fundida de la calle. Las actas de la junta y lo que la comunidad decide. Todo
                  en un solo lugar, sin llamar a las casas para preguntar y sin cuadernos que
                  después nadie puede leer.
                </p>
              </div>
            </div>

            <Link to="/adquirir" className="hero-cta-landing">
              <span className="hero-cta-circle-landing" aria-hidden="true">
                <ArrowUpRight />
              </span>
              Comience ahora
            </Link>

            <div className="hero-chip-landing">
              <span className="hero-chip-text-landing">
                <strong>Le suena el teléfono</strong>
                <span>En cuanto su visita llega a la caseta</span>
              </span>
              <span className="hero-chip-check-landing" aria-hidden="true">
                <Check strokeWidth={3} />
              </span>
            </div>
          </div>

          <div className="hero-stats-shade-landing" aria-hidden="true" />
          <dl className="hero-stats-landing">
            {cifras.map((c) => (
              <div key={c.etiqueta} className="hero-stat-landing">
                <dt>{c.etiqueta}</dt>
                <dd>{c.valor}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* ==================== NOSOTROS ==================== */}
        <section id="nosotros" className="about-landing" aria-labelledby="titulo-nosotros">
          <svg className="about-wave-landing" viewBox="0 0 600 700" preserveAspectRatio="none" aria-hidden="true">
            <path d="M600 0H70C150 60 120 170 190 250C270 340 420 330 470 430C520 530 480 620 600 700Z" />
          </svg>

          <div className="container-landing about-grid-landing">
            <div className="revelar-landing">
              <p className="eyebrow-landing">Quiénes somos</p>
              <h2 id="titulo-nosotros" className="section-title-landing">
                Administrar una residencial no debería costarle el día
              </h2>
              <p className="section-text-landing">
                En{' '}
                <a href="https://consoltechsv.com/" target="_blank" rel="noopener noreferrer" className="text-link-landing">
                  ConsolTech
                </a>{' '}
                hacemos herramientas para el día a día de las comunidades salvadoreñas. Casi
                siempre quien administra una residencial lo hace sin cobrar, después de su
                trabajo, y respondiendo mensajes hasta la noche. Este sistema le quita de encima
                lo que se puede resolver solo, y le deja por escrito lo que tiene que poder
                demostrar.
              </p>

              <ul className="benefits-landing">
                {BENEFICIOS.map((b) => (
                  <li key={b.title} className="benefit-landing">
                    <span className="benefit-icon-landing" aria-hidden="true"><b.icon /></span>
                    <div>
                      <h3>{b.title}</h3>
                      <p>{b.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <figure className="about-media-landing revelar-landing">
              <img
                src={caseta(1100, 1300, 2)}
                alt="Entrada de una residencial privada con caseta de vigilancia"
                loading="lazy"
              />
              <figcaption className="glass-badge-landing">
                <span className="glass-badge-icon-landing" aria-hidden="true"><ShieldCheck /></span>
                <span>
                  <strong>Datos aislados</strong>
                  <small>por cada residencial</small>
                </span>
              </figcaption>
            </figure>
          </div>
        </section>

        {/* ==================== TRES PERSONAS ====================
            La sección que sostiene la portada. Un condominio no compra
            «módulos»: compra que su gente deje de perseguir cosas. Y la gente
            son tres. Sin esto, los veintitantos módulos serían un muro de
            tarjetas que nadie termina de leer. */}
        <section id="personas" className="personas-landing" aria-labelledby="titulo-personas">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Para quién</p>
                <h2 id="titulo-personas" className="section-title-landing">
                  Tres personas distintas, un solo sistema
                </h2>
              </div>
              <p className="section-text-landing section-head-text-landing">
                El vecino, el guardia y la directiva quieren cosas distintas, y por eso cada uno
                entra a lo suyo. Nadie tiene que aprenderse pantallas que no le tocan.
              </p>
            </div>

            <ul className="personas-grid-landing">
              {PERSONAS.map((persona, i) => (
                <li
                  key={persona.rol}
                  className="persona-landing revelar-landing"
                  style={{ '--retardo': `${i * 90}ms` }}
                >
                  <span className="persona-icon-landing" aria-hidden="true"><persona.icon /></span>
                  <h3 className="persona-rol-landing">{persona.rol}</h3>
                  <p className="persona-lema-landing">{persona.lema}</p>
                  <ul className="persona-lista-landing">
                    {persona.puntos.map((punto) => (
                      <li key={punto}><Check strokeWidth={2.5} aria-hidden="true" /> {punto}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ==================== CÓMO FUNCIONA ==================== */}
        <section id="como-funciona" className="steps-landing" aria-labelledby="titulo-pasos">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Cómo funciona</p>
                <h2 id="titulo-pasos" className="section-title-landing">
                  Su residencial andando en tres pasos
                </h2>
              </div>
              <Link to="/adquirir" className="btn-landing btn-primary-landing">
                Comience ahora <ArrowRight />
              </Link>
            </div>

            <ol className="steps-grid-landing">
              {PASOS.map((s, i) => (
                <li key={s.title} className="step-landing revelar-landing" style={{ '--retardo': `${i * 90}ms` }}>
                  <span className="step-num-landing" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ==================== FUNCIONES ==================== */}
        <section id="funciones" className="features-landing" aria-labelledby="titulo-funciones">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Funciones</p>
                <h2 id="titulo-funciones" className="section-title-landing">
                  Doce cosas que cambian desde el primer día
                </h2>
              </div>
              <p className="section-text-landing section-head-text-landing">
                El sistema hace bastante más que esto, pero éstas son las que su gente va a notar
                la primera semana. Cada una quita una molestia que su comunidad ya tiene hoy.
              </p>
            </div>

            <ul className="features-grid-landing">
              {FUNCIONES.map((f, i) => (
                <li
                  key={f.title}
                  className={`feature-landing revelar-landing${f.highlighted ? ' feature-highlighted-landing' : ''}`}
                  style={{ '--retardo': `${(i % 4) * 70}ms` }}
                >
                  <span className="feature-icon-landing" aria-hidden="true"><f.icon /></span>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                  {f.highlighted && <span className="feature-badge-landing">Lo que más se usa</span>}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ==================== ENTREGAS ====================
            Tiene sección propia porque es la escena nueva que cualquiera
            reconoce al instante: el repartidor parado en la caseta y el
            teléfono de la casa sonando sin que nadie conteste. */}
        <section className="reminders-landing reminders-reverse-landing" aria-labelledby="titulo-entregas">
          <div className="container-landing reminders-grid-landing">
            <div className="revelar-landing">
              <p className="eyebrow-landing">Entregas</p>
              <h2 id="titulo-entregas" className="section-title-landing">
                La comida ya no se enfría en el portón
              </h2>
              <p className="section-text-landing">
                Llega el repartidor, el guardia no sabe si puede pasar, llama a la casa y nadie
                contesta. Pasa varias veces al día y termina con el pedido de vuelta. Aquí eso se
                decide antes: cada casa deja dicho de una vez qué hacer con sus entregas, y el
                guardia lo lee en el momento en que escribe el número de casa.
              </p>
              <ul className="checks-landing">
                <li><CheckCircle2 /> Dejarlas en recepción, subirlas a la puerta o no dejar pasar</li>
                <li><CheckCircle2 /> Una regla distinta para la comida y para los paquetes</li>
                <li><CheckCircle2 /> Permiso de «sólo por hoy» que se vence solo</li>
                <li><CheckCircle2 /> Y si la casa no ha dicho nada, el teléfono ya está en pantalla</li>
              </ul>
              <Link to="/adquirir" className="btn-landing btn-primary-landing">
                Comience ahora <ArrowRight />
              </Link>
            </div>

            <figure className="reminders-media-landing revelar-landing">
              <img
                src={pexels(13432261, 1100, 1300)}
                alt="Repartidor entregando un pedido en la puerta de una vivienda"
                loading="lazy"
              />
              {/* Es la instrucción que el sistema le enseña al guardia, tal cual. */}
              <figcaption className="reminders-message-landing">
                <span className="reminders-message-icon-landing" aria-hidden="true"><Truck /></span>
                <span>
                  <small>Lo que lee el guardia en la caseta</small>
                  <strong>Dejar en recepción.</strong> Es la preferencia guardada por el residente.
                </span>
              </figcaption>
            </figure>
          </div>
        </section>

        {/* ==================== AVISOS ==================== */}
        <section className="reminders-landing" aria-labelledby="titulo-avisos">
          <div className="container-landing reminders-grid-landing">
            <figure className="reminders-media-landing revelar-landing">
              <img
                src={pexels(9832140, 1100, 1300)}
                alt="Residente en su casa revisando el aviso en su teléfono"
                loading="lazy"
              />
              {/* Es el texto real que manda el sistema: sin datos de nadie. */}
              <figcaption className="reminders-message-landing">
                <span className="reminders-message-icon-landing" aria-hidden="true"><MessageCircle /></span>
                <span>
                  <small>Mensaje al visitante, por WhatsApp</small>
                  Su visita a la <strong>Casa 14</strong> quedó anotada para hoy. Aquí va su pase.
                </span>
              </figcaption>
            </figure>

            <div className="revelar-landing">
              <p className="eyebrow-landing">Avisos</p>
              <h2 id="titulo-avisos" className="section-title-landing">
                El vecino ya no reclama que no le avisaron
              </h2>
              <p className="section-text-landing">
                La queja de siempre —«entró alguien a mi casa y nadie me dijo nada»— se termina
                aquí. En cuanto la visita llega a la caseta, al residente le suena el teléfono, y
                otra vez cuando se va. Esté donde esté, y aunque en la casa no haya nadie. Y la
                directiva puede mandarle un comunicado a toda la residencial, a un polígono o a
                unas casas concretas, sin depender de que alguien reenvíe el mensaje.
              </p>
              <ul className="checks-landing">
                <li><CheckCircle2 /> Aviso al llegar y al salir, a todos los de la casa</li>
                <li><CheckCircle2 /> El pase se manda por WhatsApp, o por mensaje de texto</li>
                <li><CheckCircle2 /> Comunicados y alertas de emergencia desde la administración</li>
                <li><CheckCircle2 /> Y avisos que nadie tiene que pedir: visitante frecuente, pases sin usar</li>
              </ul>
              <Link to="/adquirir" className="btn-landing btn-primary-landing">
                Comience ahora <ArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ==================== CUANDO ALGO PASA ====================
            La otra escena que cualquier directiva reconoce: el día que hay
            que averiguar qué ocurrió. Es donde un sistema se gana el sueldo o
            queda igual que el cuaderno. */}
        <section id="respaldo" className="respaldo-landing" aria-labelledby="titulo-respaldo">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Respaldo</p>
                <h2 id="titulo-respaldo" className="section-title-landing">
                  El día que haya que averiguar qué pasó
                </h2>
                <p className="section-text-landing">
                  Robaron en una casa, hubo un pleito en la cancha, alguien entró y no debía. Ése
                  es el día en que una directiva descubre si tenía un sistema o un cuaderno.
                </p>
              </div>
            </div>

            <ul className="respaldo-grid-landing">
              <li className="respaldo-card-landing revelar-landing">
                <span className="respaldo-icon-landing" aria-hidden="true"><ScrollText /></span>
                <h3>Nada se borra</h3>
                <p>
                  Cada entrada, cada salida, cada autorización y cada cambio quedan anotados con
                  su hora y con el nombre de quien lo hizo. Y no se pueden editar después: si algo
                  se corrige, la corrección queda como un registro nuevo. Lo que se lee en una
                  asamblea es lo que pasó, no lo que alguien recuerda.
                </p>
              </li>
              <li className="respaldo-card-landing revelar-landing" style={{ '--retardo': '90ms' }}>
                <span className="respaldo-icon-landing" aria-hidden="true"><Search /></span>
                <h3>La respuesta sale en segundos</h3>
                <p>
                  Se escribe un nombre a medias, un número de casa, tres dígitos de una placa o un
                  teléfono, y sale todo lo relacionado: quién vino, a qué hora, en qué llegó y
                  quién lo dejó entrar. También el historial completo de una persona o de una
                  casa, cuando la pregunta es más grande.
                </p>
              </li>
              <li className="respaldo-card-landing revelar-landing" style={{ '--retardo': '180ms' }}>
                <span className="respaldo-icon-landing" aria-hidden="true"><Siren /></span>
                <h3>Lo que pasó queda reportado</h3>
                <p>
                  El guardia levanta el incidente ahí mismo, con su tipo y su gravedad, y la
                  administración le da seguimiento hasta cerrarlo. Si la comunidad lo decide, se
                  puede guardar fotografía del visitante, de su documento o de la placa —viene
                  apagado de fábrica: ésa es una decisión de la residencial, no del programa—.
                </p>
              </li>
            </ul>
          </div>
        </section>

        {/* ==================== SEGURIDAD ==================== */}
        <section id="seguridad" className="pricing-landing" aria-labelledby="titulo-seguridad">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Seguridad</p>
                <h2 id="titulo-seguridad" className="section-title-landing">
                  Lo que pasa en su residencial se queda en su residencial
                </h2>
                <p className="section-text-landing">
                  Una lista de quién visita a quién, a qué hora y en qué carro dice mucho de la
                  vida de la gente. Eso no debería andar suelto, y menos ahora que la Ley de
                  Protección de Datos ya está vigente en El Salvador. El sistema está hecho para
                  que esa lista no la pueda leer nadie más que su comunidad.
                </p>
              </div>
            </div>

            <div className="pricing-grid-landing">
              <article className="plan-landing revelar-landing">
                <h3 className="plan-name-landing">Guardado bajo llave</h3>
                <p className="plan-desc-landing">
                  Los nombres, los teléfonos y los números de casa no se guardan tal cual. Alguien
                  que lograra entrar al servidor, o hacerse con una copia de seguridad, no vería
                  más que letras sin sentido.
                </p>
                <ul className="plan-features-landing">
                  <li><Check strokeWidth={2.5} /> Los datos de su gente, ilegibles para terceros</li>
                  <li><Check strokeWidth={2.5} /> Ni nosotros podemos ver una contraseña</li>
                  <li><Check strokeWidth={2.5} /> Copias de seguridad todos los días</li>
                  <li><Check strokeWidth={2.5} /> Y el sistema sigue igual de rápido para buscar</li>
                </ul>
              </article>

              <article className="plan-landing plan-featured-landing revelar-landing" style={{ '--retardo': '90ms' }}>
                <span className="plan-badge-landing">Aislamiento</span>
                <h3 className="plan-name-landing">Su comunidad, aparte</h3>
                <p className="plan-desc-landing">
                  Otras residenciales usan este mismo sistema y jamás ven nada de la suya, ni
                  aunque tengan el mismo número de casa y el mismo polígono. No es que la pantalla
                  se los esconda: sus datos están separados de raíz.
                </p>
                <ul className="plan-features-landing">
                  <li><Check strokeWidth={2.5} /> Sus visitas no las ve ninguna otra comunidad</li>
                  <li><Check strokeWidth={2.5} /> Los avisos llegan solo a su gente</li>
                  <li><Check strokeWidth={2.5} /> Su guardia solo puede validar pases de aquí</li>
                  <li><Check strokeWidth={2.5} /> Y su configuración es suya, no la de todos</li>
                </ul>
                <Link to="/adquirir" className="btn-landing btn-light-landing btn-block-landing">
                  Comience ahora <ArrowRight />
                </Link>
              </article>
            </div>
          </div>
        </section>

        {/* ==================== PRECIOS ==================== */}
        <section id="precios" className="pricing-landing" aria-labelledby="titulo-precios">
          <div className="container-landing">
            <div className="section-head-landing revelar-landing">
              <div>
                <p className="eyebrow-landing">Precios</p>
                <h2 id="titulo-precios" className="section-title-landing">
                  Un precio por residencial, no por casa
                </h2>
                <p className="section-text-landing">
                  Los dos planes traen el sistema completo. Lo que cambia es el tamaño de su
                  comunidad, no las funciones. Sin contratos a largo plazo: cancele cuando quiera.
                </p>
              </div>

              <div className="billing-toggle-landing" role="group" aria-label="Periodo de facturación">
                <button
                  type="button"
                  className={`toggle-btn-landing${!anual ? ' toggle-active-landing' : ''}`}
                  onClick={() => setAnual(false)}
                  aria-pressed={!anual}
                >
                  Mensual
                </button>
                <button
                  type="button"
                  className={`toggle-btn-landing${anual ? ' toggle-active-landing' : ''}`}
                  onClick={() => setAnual(true)}
                  aria-pressed={anual}
                >
                  Anual <span className="save-badge-landing">2 meses gratis</span>
                </button>
              </div>
            </div>

            <div className="pricing-grid-landing">
              <article className="plan-landing revelar-landing">
                <h3 className="plan-name-landing">Base</h3>
                <p className="plan-desc-landing">
                  Para residenciales de un solo sector. Todo el sistema, sin nada recortado:
                  la caseta, la vida de la comunidad y la administración.
                </p>
                <p className="plan-price-landing">
                  <span className="plan-amount-landing">
                    ${planBase ? planBase.price.toLocaleString('es-SV') : '—'}
                  </span>
                  <span className="plan-period-landing">/{periodo}</span>
                </p>
                {anual && planBase && (
                  <p className="plan-savings-landing">
                    Ahorra ${((planBase.price / 10) * 2).toLocaleString('es-SV')} al año
                  </p>
                )}
                <ul className="plan-features-landing">
                  {(planBase?.features || []).map((f) => (
                    <li key={f}><Check strokeWidth={2.5} /> {f}</li>
                  ))}
                </ul>
                <Link
                  to={`/adquirir?plan=basic_${anual ? 'annual' : 'monthly'}`}
                  className="btn-landing btn-outline-landing btn-block-landing"
                >
                  Comience ahora <ArrowRight />
                </Link>
              </article>

              <article className="plan-landing plan-featured-landing revelar-landing" style={{ '--retardo': '90ms' }}>
                <span className="plan-badge-landing">Recomendado</span>
                <h3 className="plan-name-landing">Completo</h3>
                <p className="plan-desc-landing">
                  Para comunidades grandes, con varios polígonos o etapas, que además quieren
                  avisos por SMS, reportes para la junta y su propia marca.
                </p>
                <p className="plan-price-landing">
                  <span className="plan-amount-landing">
                    ${planCompleto ? planCompleto.price.toLocaleString('es-SV') : '—'}
                  </span>
                  <span className="plan-period-landing">/{periodo}</span>
                </p>
                {anual && planCompleto && (
                  <p className="plan-savings-landing">
                    Ahorra ${((planCompleto.price / 10) * 2).toLocaleString('es-SV')} al año
                  </p>
                )}
                <ul className="plan-features-landing">
                  {(planCompleto?.features || []).map((f) => (
                    <li key={f}><Check strokeWidth={2.5} /> {f}</li>
                  ))}
                </ul>
                <Link
                  to={`/adquirir?plan=custom_${anual ? 'annual' : 'monthly'}`}
                  className="btn-landing btn-light-landing btn-block-landing"
                >
                  Comience ahora <ArrowRight />
                </Link>
              </article>
            </div>

            <p className="section-text-landing" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              ¿Prefiere hablar antes de pagar?{' '}
              <Link to="/solicitar" className="text-link-landing">Cuéntenos de su residencial</Link>{' '}
              y le escribimos.
            </p>
          </div>
        </section>

        {/* ==================== LLAMADA FINAL ==================== */}
        <section id="contacto" className="final-cta-landing" aria-labelledby="titulo-cta">
          <div className="container-landing">
            <div className="final-card-landing revelar-landing">
              <img
                className="final-photo-landing"
                src={caseta(1600, 900, 1.2, 0.68, 0.6)}
                alt=""
                loading="lazy"
              />
              <div className="final-shade-landing" aria-hidden="true" />
              <div className="final-content-landing">
                <h2 id="titulo-cta" className="final-title-landing">
                  ¿Listo para ordenar la entrada de su residencial?
                </h2>
                <p>Cuéntenos de su comunidad y la activamos.</p>
                <div className="final-actions-landing">
                  <Link to="/adquirir" className="btn-landing btn-light-landing">
                    Comience ahora <ArrowRight />
                  </Link>
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="hero-cta-landing">
                    <span className="hero-cta-circle-landing" aria-hidden="true"><Phone /></span>
                    Hablemos · +503 6896-0231
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ==================== PIE ==================== */}
      <footer className="footer-landing">
        <div className="container-landing footer-grid-landing">
          <div className="footer-brand-landing">
            <span className="brand-landing">
              <ShieldCheck className="brand-icon-landing" strokeWidth={2.25} aria-hidden="true" />
              <span>Control Residencial</span>
            </span>
            <p>
              Control de acceso y administración para residenciales y condominios en El Salvador.
            </p>
          </div>
          <div className="footer-col-landing">
            <h2>Navegación</h2>
            <a href="#personas" onClick={irA('personas')}>Para quién</a>
            <a href="#funciones" onClick={irA('funciones')}>Funciones</a>
            <a href="#respaldo" onClick={irA('respaldo')}>Respaldo</a>
            <a href="#seguridad" onClick={irA('seguridad')}>Seguridad</a>
          </div>
          <div className="footer-col-landing">
            <h2>Acceso</h2>
            <Link to="/login">Iniciar sesión</Link>
            <Link to="/adquirir">Comience ahora</Link>
          </div>
          <div className="footer-col-landing">
            <h2>Contacto</h2>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"><Phone /> +503 6896-0231</a>
            <a href="mailto:info@consoltechsv.com"><Mail /> info@consoltechsv.com</a>
          </div>
        </div>
        <div className="container-landing footer-bottom-landing">
          <p>© {new Date().getFullYear()} Control Residencial. Todos los derechos reservados.</p>
          <p>
            Desarrollado por{' '}
            <a href="https://consoltechsv.com/" target="_blank" rel="noopener noreferrer">ConsolTech</a>
          </p>
        </div>
      </footer>

      <a
        href={WHATSAPP_MENSAJE}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float-landing"
        aria-label="Contactar por WhatsApp"
      >
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16.004c0 3.5 1.129 6.744 3.047 9.383L1.054 31.5l6.328-2.012A15.9 15.9 0 0 0 16.004 32C24.826 32 32 24.826 32 16.004 32 7.176 24.826 0 16.004 0zm9.35 22.606c-.39 1.1-2.296 2.106-3.2 2.24-.82.12-1.86.17-3-.188a27 27 0 0 1-2.716-1.004c-4.784-2.066-7.908-6.904-8.148-7.222-.24-.318-1.946-2.59-1.946-4.94s1.23-3.504 1.668-3.982c.438-.478.956-.598 1.274-.598.318 0 .636.002.914.016.294.014.688-.112 1.076.82.39.94 1.326 3.224 1.444 3.46.118.24.198.518.04.836-.16.318-.24.516-.478.796-.24.278-.504.622-.72.836-.24.238-.488.496-.21.974.278.478 1.238 2.04 2.66 3.306 1.826 1.628 3.366 2.132 3.844 2.372.478.24.756.2 1.034-.12.278-.318 1.196-1.394 1.514-1.872.318-.478.636-.398 1.074-.238.438.16 2.782 1.312 3.26 1.552.478.238.796.358.914.558.12.198.12 1.152-.27 2.254z" fill="currentColor" />
        </svg>
      </a>

      {/* Acción fija en teléfono: la solicitud siempre a mano */}
      <div className={`sticky-cta-landing${scrolled ? ' sticky-visible-landing' : ''}`}>
        <Link to="/adquirir" className="btn-landing btn-primary-landing btn-block-landing">
          Comience ahora <ArrowRight />
        </Link>
      </div>
    </div>
  );
}

export default Landing;
